import { Client, Conversation, Message, Paginator } from '@twilio/conversations'

import { logMessage } from 'src/api/chat'
import { showLocalNotification } from 'src/services/notification/localNotification'
import { store } from 'src/store/configureStore'
import { onReceiveMessage } from 'src/store/slices/connection'
import { incrementUnreadCount, setUnreadCount, setLastMessage } from 'src/store/slices/messages'

import { onTokenExpired } from './listeners'

const SYSTEM_INBOX_ROLE = 'system_inbox'

const getConversationAttributes = (conversation: Conversation): Record<string, unknown> => {
  const attrs = conversation.attributes
  return attrs && typeof attrs === 'object' ? (attrs as Record<string, unknown>) : {}
}

export const isSystemConversation = (conversation: Conversation): boolean => {
  return getConversationAttributes(conversation).role === SYSTEM_INBOX_ROLE
}

const handleSystemMessage = (message: Message) => {
  const attrs = (message.attributes ?? {}) as Record<string, unknown>
  const eventType = typeof attrs.event_type === 'string' ? attrs.event_type : 'unknown'
  window.dispatchEvent(
    new CustomEvent('connectup:system-message', {
      detail: { eventType, attributes: attrs, body: message.body ?? '' },
    })
  )
}

let client: Client | undefined

// Helper function to get current user
const getCurrentUser = () => {
  return store.getState().account.currentUser?.email ?? ''
}

// Keep track of all conversations
const conversations = new Map<string, Conversation>()

// Keep track of message paginators for each conversation
const messagePaginators = new WeakMap<Conversation, Paginator<Message>>()

// Default page size for message pagination
const DEFAULT_PAGE_SIZE = 30

export const initConversationClient = (token: string) => {
  if (client) {
    return client
  }
  client = new Client(token)
  addClientListeners(client)
  return client
}

export const getConversationClient = () => client!

const addClientListeners = (client: Client) => {
  client.on('tokenAboutToExpire', () => void onTokenExpired(client))
  client.on('tokenExpired', () => void onTokenExpired(client))
  client.on('initialized', () => {
    void (async () => {
      const conversationsList = await client.getSubscribedConversations()
      for (const conversation of conversationsList.items) {
        conversations.set(conversation.sid, conversation)
      }
    })()
  })
  client.on('conversationAdded', (conversation) => {
    conversations.set(conversation.sid, conversation)
  })
  client.on('messageAdded', (message) => {
    // eslint-disable-next-line @typescript-eslint/require-await
    void (async () => {
      const conversation = message.conversation
      const currentUser = getCurrentUser()

      if (isSystemConversation(conversation)) {
        handleSystemMessage(message)
        return
      }

      // Only mark as unread if message is from another user
      const isUnread = message.author !== currentUser

      // Update last message in Redux
      store.dispatch(
        setLastMessage({
          conversationId: conversation.sid,
          message: {
            sender: message.author ?? '',
            content: message.body ?? '',
            timestamp: message.dateCreated ? message.dateCreated.getTime() : Date.now(),
          },
        })
      )

      if (store.getState().connection.selectedConnection?.sid === conversation.sid) {
        // If chat is open, just dispatch the message
        const appMessage = convertConversationMessageToAppMessage(message)
        store.dispatch(onReceiveMessage(appMessage))
      } else if (isUnread) {
        // If chat is not open, increment unread count
        store.dispatch(incrementUnreadCount(conversation.sid))

        // Respect the master notifications toggle for foreground banners.
        // (OS-level lockscreen banner is delivered by Twilio directly and is
        // not controlled here — see the account settings notification toggle.)
        const notificationsEnabled =
          store.getState().account.currentUser?.notificationsEnabled ?? true

        if (notificationsEnabled) {
          // Foreground local banner so user notices while app open on another screen
          void showLocalNotification({
            title: (getConversationAttributes(conversation).name as string) ?? 'New message',
            body: message.body ?? '',
            extra: { conversation_sid: conversation.sid },
          })
        }
      }
    })()
  })
}

export const buildAndSendMessage = async ({
  sid,
  message,
  userId,
}: {
  sid: string
  message: string
  userId: string
}) => {
  const conversation = await getConversationClient().getConversationBySid(sid)
  const messageIndex = await conversation
    .prepareMessage()
    .setBody(message)
    .setAttributes({ author: userId })
    .buildAndSend()

  // Log message to backend for analytics (fire-and-forget)
  // Get the sent message by index to retrieve its SID
  if (messageIndex !== null) {
    conversation
      .getMessages(1, messageIndex, 'forward')
      .then((paginator) => {
        const sentMessage = paginator.items[0]
        if (sentMessage?.sid) {
          void logMessage({
            conversation_sid: sid,
            message_sid: sentMessage.sid,
            message_type: 'text',
          } as Parameters<typeof logMessage>[0])
        }
      })
      .catch((err) => console.warn('Failed to log message:', err))
  }

  return messageIndex
}

export const getConversation = async (sid: string) => {
  const conversation = await getConversationClient().getConversationBySid(sid)
  conversations.set(sid, conversation)

  // Get initial page of messages with pagination
  const paginator = await conversation.getMessages(DEFAULT_PAGE_SIZE)

  // Store the paginator for future use
  messagePaginators.set(conversation, paginator)

  // Set last message in Redux if there are messages
  if (paginator.items.length > 0) {
    const last = paginator.items[paginator.items.length - 1]
    store.dispatch(
      setLastMessage({
        conversationId: sid,
        message: {
          sender: last.author ?? '',
          content: last.body ?? '',
          timestamp: last.dateCreated ? last.dateCreated.getTime() : Date.now(),
        },
      })
    )
  }

  return {
    sid,
    status: 'accepted' as 'pending' | 'accepted' | 'rejected',
    messages: paginator.items.map(convertConversationMessageToAppMessage),
    hasMore: paginator.hasPrevPage,
    isLoadingMore: false,
  }
}

export const convertConversationMessageToAppMessage = (message: Message) => {
  const conversation = message.conversation
  return {
    sid: message.sid,
    ...(message.body ? { message: message.body } : {}),
    ...(message.attributes ? { attributes: message.attributes as Record<string, string> } : {}),
    ...(message.dateCreated ? { timestamp: message.dateCreated.getTime() } : {}),
    ...(message.author ? { author: message.author } : {}),
    conversationSid: conversation.sid,
  }
}

// Load more messages for pagination
export const loadMoreMessages = async (sid: string) => {
  const conversation = conversations.get(sid)
  if (!conversation) {
    throw new Error('Conversation not found')
  }

  const currentPaginator = messagePaginators.get(conversation)
  if (!currentPaginator?.hasPrevPage) {
    return {
      messages: [],
      hasMore: false,
    }
  }

  // Get previous page (older messages)
  const newPaginator = await currentPaginator.prevPage()

  // Update stored paginator
  messagePaginators.set(conversation, newPaginator)

  // Convert messages to app format
  const messages = newPaginator.items.map(convertConversationMessageToAppMessage)

  return {
    messages,
    hasMore: newPaginator.hasPrevPage,
  }
}

// Add new function to mark messages as read
export const markMessagesAsRead = (conversation: Conversation) => {
  // Update Redux store to clear unread count
  store.dispatch(
    setUnreadCount({
      conversationId: conversation.sid,
      count: 0,
    })
  )
}
